import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import {
  Button,
  GenericDialog,
  GenericCombobox,
  NumericInputGroup,
  InputGroup,
  Alert
} from '@/common'
import { OrganizationDocumentUpload } from '@/shared/components'
import { UnitLabel } from '@/shared/config/config'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { toast } from 'react-hot-toast'
import { useGetPrecursorEmissionReports } from '@/hooks/api/v2/precursorEmissionReports'
import {
  PrecursorMaterial,
  useCreatePrecursorMaterial,
  useUpdatePrecursorMaterial
} from '@/hooks/api/v2/precursorMaterials'
import { useSession } from '@/shared/hooks/useSession'
import useI18n from '@/shared/hooks/useI18n'
import { useReEvaluateActivityLevelValidationForMethodEmissionReport } from '@/hooks/api/emissionReports'
import { Link } from 'react-router-dom'

type State = {
  quantityInTonnes: string
  proofFilePath: string | null
  precursorEmissionReportId: string | null
  details: string | null
  internalId: string | null
}

const getInitialState = (initialState?: PrecursorMaterial): State => ({
  quantityInTonnes: initialState?.quantityInTonnes?.toString() ?? '',
  proofFilePath: initialState?.proofFilePath ?? null,
  precursorEmissionReportId: initialState?.precursorEmissionReportId ?? null,
  details: initialState?.details ?? null,
  internalId: initialState?.internalId ?? null
})

export default function PurchasedPrecursorDialog({
  emissionReportId,
  methodEmissionReportId,
  installationId,
  isOpen,
  close,
  reportStartDate,
  initialState
}: {
  emissionReportId: string
  methodEmissionReportId: string
  installationId: string
  isOpen: boolean
  close: () => void
  reportStartDate: string
  reportEndDate: string
  initialState?: PrecursorMaterial
}) {
  const { t, dateLongFormatterMonthYear } = useI18n()
  const organizationId = useSession().session.organization.id
  const [isEditMode, setIsEditMode] = useState(false)
  const [state, setState] = useState<State>(getInitialState())
  const [isUploadingProof, setIsUploadingProof] = useState(false)

  useEffect(() => {
    setIsEditMode(!!initialState)
    if (initialState) {
      setState(getInitialState(initialState))
      return
    }
    setState(getInitialState())
  }, [initialState])

  const { quantityInTonnes, precursorEmissionReportId, internalId, proofFilePath } = state

  const precursorEmissionReportsQuery = useGetPrecursorEmissionReports(organizationId ?? '')
  const precursorEmissionReports = useMemo(
    () => precursorEmissionReportsQuery.data || [],
    [precursorEmissionReportsQuery.data]
  )
  const isLoadingPrecursorEmissionReports = precursorEmissionReportsQuery.isLoading

  const { mutateAsync: createPrecursorMaterial, isPending: isCreatingPrecursorMaterial } =
    useCreatePrecursorMaterial(installationId)
  const { mutateAsync: updatePrecursorMaterial, isPending: isUpdatingPrecursorMaterial } =
    useUpdatePrecursorMaterial(installationId)

  const {
    mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport,
    isPending: isUpdatingActivityLevelValidation
  } = useReEvaluateActivityLevelValidationForMethodEmissionReport(
    emissionReportId,
    methodEmissionReportId
  )

  const { checkPermission } = usePermissions()

  const options = useMemo(
    () =>
      precursorEmissionReports
        .filter(precursorEmissionReport => {
          return precursorEmissionReport.deletedAt === null
        })
        .map(precursorEmissionReport => {
          const supplierName = precursorEmissionReport.precursorSupplier.name
          const startDate = dateLongFormatterMonthYear(new Date(precursorEmissionReport.startDate))
          const endDate = dateLongFormatterMonthYear(new Date(precursorEmissionReport.endDate))
          const goodsDescription = precursorEmissionReport.goodsDescription

          const label = `${supplierName} / ${startDate} - ${endDate} / ${goodsDescription}`
          return {
            label,
            value: precursorEmissionReport.id
          }
        }) ?? [],
    [precursorEmissionReports, dateLongFormatterMonthYear]
  )

  const canAddPrecursors = checkPermission('precursor', 'create')

  const selectedPrecursorEmissionReport = precursorEmissionReports.find(
    report => report.id === state.precursorEmissionReportId
  )
  const selectedOption = options.find(option => option.value === state.precursorEmissionReportId)

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    evt => setState({ ...state, [evt.target.name]: evt.target.value || null }),
    [setState, state]
  )

  const resetState = useCallback(() => {
    setState(getInitialState(initialState))
    setIsEditMode(!!initialState)
  }, [initialState])

  const onAddClick = useCallback(async () => {
    if (!selectedPrecursorEmissionReport) {
      return
    }

    try {
      await createPrecursorMaterial({
        stockChangeType: 'ALLOCATED',
        internalId,
        date: reportStartDate,
        quantityInTonnes: parseFloat(quantityInTonnes),
        proofFilePath,
        details: selectedPrecursorEmissionReport.goodsDescription,
        emissionReportId,
        precursorEmissionReportId: selectedPrecursorEmissionReport.id
      })
      toast.success(t('report:precursors.dialog.toast.addedSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:precursors.dialog.toast.addedError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    quantityInTonnes,
    t,
    createPrecursorMaterial,
    proofFilePath,
    close,
    reportStartDate,
    internalId,
    resetState,
    selectedPrecursorEmissionReport,
    emissionReportId,
    reEvaluateActivityLevelValidationForMethodEmissionReport
  ])

  const onEditClick = useCallback(async () => {
    if (!initialState) {
      return
    }

    try {
      await updatePrecursorMaterial({
        precursorMaterialId: initialState.id,
        updatePayload: {
          emissionReportId,
          stockChangeType: initialState.stockChangeType,
          internalId,
          date: initialState.date,
          quantityInTonnes: parseFloat(quantityInTonnes),
          proofFilePath,
          details: initialState.details
        }
      })
      toast.success(t('report:precursors.dialog.toast.updateSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:precursors.dialog.toast.updatedError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    initialState,
    updatePrecursorMaterial,
    internalId,
    quantityInTonnes,
    proofFilePath,
    emissionReportId,
    reEvaluateActivityLevelValidationForMethodEmissionReport,
    resetState,
    close,
    t
  ])

  const isAddOrEditDisabled =
    !quantityInTonnes ||
    !precursorEmissionReportId ||
    !canAddPrecursors ||
    isCreatingPrecursorMaterial ||
    isUpdatingPrecursorMaterial ||
    isUpdatingActivityLevelValidation ||
    isUploadingProof

  useEffect(() => {
    setState(getInitialState(initialState))
  }, [initialState])

  return (
    <GenericDialog
      size="3xl"
      isOpen={isOpen}
      title={
        isEditMode
          ? t('report:precursors.dialog.editTitle')
          : t('report:precursors.dialog.addTitle')
      }
      close={close}
      subtitle={t('report:precursors.dialog.subtitle')}
    >
      <div className="space-y-4">
        <div>
          <GenericCombobox
            disabled={isLoadingPrecursorEmissionReports}
            label={t('report:precursors.dialog.fields.supplierName.label')}
            value={selectedOption || null}
            onChange={evt => setState({ ...state, precursorEmissionReportId: evt?.value ?? null })}
            items={options}
            noItemsMessage={t('report:precursors.dialog.fields.supplierName.noItemsMessage')}
            hint={
              <span>
                <Trans
                  i18nKey="report:precursors.dialog.fields.supplierName.hint"
                  components={{
                    Link: <Link className="text-green-600 underline" to="/suppliers" />
                  }}
                />
              </span>
            }
          />
        </div>
        <div className="grid grid-cols-3 gap-4 items-center">
          <div className="col-span-2">
            <InputGroup
              label={t('report:precursors.dialog.fields.internalId.label')}
              value={state.internalId ?? ''}
              onChange={evt => setState({ ...state, internalId: evt.target.value })}
            />
          </div>
          <NumericInputGroup
            disabled={!precursorEmissionReportId}
            name="quantityInTonnes"
            suffix={UnitLabel.t}
            label={t('report:precursors.dialog.fields.quantityInTonnes.label')}
            value={quantityInTonnes}
            onChange={handleChange}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
          />
        </div>
        <OrganizationDocumentUpload
          label={t('report:precursors.dialog.fields.uploadEmissionProof.label')}
          fileName={proofFilePath}
          onDocumentUploaded={file => setState(st => ({ ...st, proofFilePath: file }))}
          onDocumentDeleted={() => setState(st => ({ ...st, proofFilePath: null }))}
          disabled={!precursorEmissionReportId}
          onUploading={setIsUploadingProof}
        />
        <Alert type="info" className="whitespace-pre-wrap">
          <Trans
            i18nKey="report:precursors.dialog.alerts.disclaimer"
            components={{
              strong: <strong className="font-semibold" />
            }}
          />
        </Alert>
        <footer className="gap-2 flex justify-end">
          <Button
            variant="neutral"
            onClick={close}
            className="min-w-24"
            disabled={isUploadingProof}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={isAddOrEditDisabled}
            onClick={isEditMode ? onEditClick : onAddClick}
            isLoading={isCreatingPrecursorMaterial || isUpdatingPrecursorMaterial}
            className="min-w-24"
          >
            {isEditMode ? t('common:actions.edit') : t('common:actions.add')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
